import { Dayjs } from 'dayjs';
import moment from 'moment';

import { SHIPMENT_STATUS } from '@Constants/Shipment';
import { ShipmentObj } from '@Reducers/Invoice/Type';

import { convertWeightFormKGtoLB } from '../../../../utils/convertWeight';
import formatDate from '../../../../utils/formatDate';
import { formatNumber } from '../../../../utils/formatNumber';
import { AccountHolderUnbilledData } from '../../Type';

export interface TaxInformationOption {
  display: string;
  value: string | number;
  key: string;
}

export interface FilterType {
  currentPage: number;
  perPage: number;
  fromDate: string;
  toDate: string;
  searchKey?: string;
  searchValue: string | undefined;
  systems: string[];
  type: string[];
}

export type SelectedInvoicesMap = { [x: string]: boolean };

export type InvConfig = {
  invoiceDate?: Dayjs | null;
  dueDate?: Dayjs | null;
  taxBreakdown?: string[];
  splitTax?: string[];
  sacCode?: string[];
  billTo?: string[];
  billname?: string;
  billaddress?: string;
  taxname?: string;
  taxno?: string;
};

export type SelectedParamType = {
  accountNumber: string;
  shipmentRouteType: string;
  _systemId: string;
  systemName: string;
  shipments: ShipmentObj[];
  uniqueKey?: string;
  _userId?: string;
};

export interface MappedShipmentRow {
  id: number;
  _id: string;
  date: string;
  shipmentNo: string;
  status: string;
  shipper: ShipmentObj['_shipperId'];
  consignee: ShipmentObj['_consigneeId'];
  weight: string | number;
  packages: number;
  totalAmount: string;
  rawAmount: number;
  currency: string;
  isDisabled: string | undefined;
  serviceName: string;
  awb: string;
}

export const SearchFilterList = [
  { label: 'Account No.', value: 'account_no' },
  { label: 'Name', value: 'name' },
  { label: 'Email', value: 'email' },
];

export const billToOptions = [
  { display: 'Customer', value: 'customer' },
  { display: 'Third Party', value: 'third_party' },
];

export const splitTaxOptions = [
  {
    display: 'Split Tax',
    value: 'true',
  },
  {
    display: 'Full Tax',
    value: 'false',
  },
];

export const ShipmentTypeList = [
  {
    value: 'import',
    display: 'Import',
  },
  {
    value: 'export',
    display: 'Export',
  },
  {
    value: 'domestic',
    display: 'Domestic',
  },
];

export const initialDateRange = {
  startDate: moment().subtract(1, 'months').startOf('day').toDate(),
  endDate: moment().endOf('day').toDate(),
};

export const initialInvConfig: InvConfig = {
  invoiceDate: null,
  dueDate: null,
  billTo: [],
};

export function convertToBoolean(input: string[] | undefined | null): boolean | null {
  if (!Array.isArray(input)) {
    return null;
  }

  if (input.length === 1) {
    const value = input[0].toLowerCase();
    if (value === 'true') return true;
    if (value === 'false') return false;
  }

  return null;
}

export function getIsWyngsMy(): boolean {
  if (typeof window === 'undefined') return false;
  const subdomain = window.location.hostname.split('.')[0];
  return subdomain === 'wyngs-my';
}

export function getTaxBreakDownObject(keys: string[] | undefined, data: TaxInformationOption[]) {
  if (!Array.isArray(keys)) {
    console.warn('getTaxBreakDownObject: keys is not an array:', keys);
    return undefined;
  }

  for (const key of keys) {
    const matchedItem = data.find((item: TaxInformationOption) => item.key === key);

    if (matchedItem) {
      return {
        display: matchedItem.display,
        value: matchedItem.value,
        key: matchedItem.key,
      };
    }
  }

  return undefined;
}

export function getAccountGroupKey(data: {
  uniqueKey?: string;
  accountNumber?: string;
  shipmentRouteType?: string;
  _systemId?: { _id?: string } | string;
}): string {
  if (data?.uniqueKey) return data.uniqueKey;
  const sysId = typeof data?._systemId === 'string' ? data._systemId : data?._systemId?._id || '';
  return `${data?.accountNumber || ''}_${data?.shipmentRouteType || ''}_${sysId}`;
}

export function isSameAccountEntry(a: SelectedParamType, b: SelectedParamType | AccountHolderUnbilledData): boolean {
  const bSystemId = typeof b._systemId === 'string' ? b._systemId : b._systemId?._id;
  return (
    a.accountNumber === b.accountNumber && a.shipmentRouteType === b.shipmentRouteType && a._systemId === bSystemId
  );
}

export function mapShipmentsToGridRows(shipments: ShipmentObj[], orgName: string, locale: string): MappedShipmentRow[] {
  return (shipments || []).map((s, i: number) => ({
    id: i + 1,
    _id: s._id,
    date: moment(s.createdAt).format('DD/MM/YYYY'),
    shipmentNo: s.shipmentNumber,
    status: SHIPMENT_STATUS[s.status]?.label || s.status || '-',
    shipper: s._shipperId,
    consignee: s._consigneeId,
    weight:
      orgName === 'Cargo Solutions Network'
        ? convertWeightFormKGtoLB(s?.sellingPricing?.displayChargeableWeight, orgName)
        : s?.sellingPricing?.displayChargeableWeight + 'kg',
    packages: s?._packageId?.box?.length || 0,
    totalAmount: formatNumber(s?.sellingPricing?.totalPricing || 0, locale),
    rawAmount: s?.sellingPricing?.totalPricing || 0,
    currency: s?.sellingPricing?.currency || s?._packageId?.currency || '',
    isDisabled: s.carrierDetails && s.carrierDetails?.awb,
    serviceName: s?.sellingPricing?.serviceName || '-',
    awb: s?.carrierDetails?.awb || '-',
  }));
}

export function calculateSelectedPricingSum(selectedRows: SelectedParamType[]): number {
  if (!selectedRows?.length) return 0;
  return selectedRows.reduce((sum, entry) => {
    const entryShipments = entry?.shipments || [];
    const total = entryShipments.reduce((acc, shipment) => acc + (shipment?.sellingPricing?.totalPricing || 0), 0);
    return sum + total;
  }, 0);
}

export function updateSelectedAccountRowsList(
  prevRows: SelectedParamType[],
  params: SelectedParamType,
): SelectedParamType[] {
  const { accountNumber, shipmentRouteType, _systemId, shipments } = params;
  const filtered = prevRows.filter(
    item =>
      item.accountNumber !== accountNumber ||
      item.shipmentRouteType !== shipmentRouteType ||
      item._systemId !== _systemId,
  );
  if (shipments?.length) {
    filtered.push(params);
  }
  return filtered;
}

export function validateInvoiceConfig(
  config: InvConfig,
  accountRows: SelectedParamType[],
  isWyngsMy: boolean,
): string[] {
  const { dueDate, invoiceDate, billTo, billaddress, billname, taxname } = config;
  const missingFields: string[] = [];
  const totalShipments = accountRows?.reduce((acc, r) => acc + (r?.shipments?.length || 0), 0) || 0;

  if (isWyngsMy) {
    if (!invoiceDate) missingFields.push('Invoice Date');
    if (!dueDate) missingFields.push('Due Date');
    if (!accountRows || accountRows.length === 0 || totalShipments === 0) {
      missingFields.push('At least one shipment');
    }
  } else {
    if (!invoiceDate) missingFields.push('Invoice Date');
    if (!dueDate) missingFields.push('Due Date');

    if (!accountRows || accountRows.length === 0 || totalShipments === 0) {
      missingFields.push('At least one shipment');
    }

    if (billTo?.[0] === 'third_party') {
      if (!billname || !billname.trim()) missingFields.push('Third Party Name');
      if (!billaddress || !billaddress.trim()) missingFields.push('Third Party Address');
      if (!taxname || !taxname.trim()) missingFields.push('Third Party Tax Name');
    }
  }

  return missingFields;
}

export function buildAccountInvoicePayload(
  config: InvConfig,
  accountRows: SelectedParamType[],
  taxInformationOptions: TaxInformationOption[],
) {
  const { sacCode, taxBreakdown, dueDate, invoiceDate, billTo, billaddress, billname, taxname, taxno, splitTax } =
    config;

  const accountData = accountRows.map((item: SelectedParamType) => ({
    accountNo: item.accountNumber,
    shipmentRouteType: item.shipmentRouteType,
    _systemId: item._systemId,
    _shipmentIds: item.shipments.map(s => s._id),
    _shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
  }));

  const payload = {
    dueDate: formatDate(dueDate),
    invoiceDate: formatDate(invoiceDate),
    tax: getTaxBreakDownObject(taxBreakdown, taxInformationOptions),
    sacCode: sacCode?.[0],
    accountData,
    splitTax: convertToBoolean(splitTax as string[]),
    billToType: '',
    thirdPartyInfo: {},
  };

  if (billTo?.[0] === 'third_party') {
    payload.billToType = billTo[0];
    payload.thirdPartyInfo = {
      name: billname,
      address: billaddress,
      taxInfoList: [{ key: taxname, value: taxno }],
    };
  }

  return payload;
}
